import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ConfigRoutes, getPath } from "../../routes/routes.path";
import {
  InvitationsService,
  type InvitationScanResponse,
} from "../../shared/services/invitations.service";
import { ButtonLarge, ButtonLink } from "../../shared/uikit";

export const InvitationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const qrToken = searchParams.get("qrToken") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<InvitationScanResponse | null>(
    null,
  );
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch invitation details on load
  useEffect(() => {
    if (!qrToken) {
      setError("No QR token provided");
      setLoading(false);
      return;
    }

    const fetchInvitation = async () => {
      try {
        const result = await InvitationsService.scanInvitation(qrToken);
        setInvitation(result.data);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to load invitation";
        if (message.includes("expired") || message.includes("404")) {
          setError("Invitation expired or not found");
        } else if (
          message.includes("accepted") ||
          message.includes("confirmed")
        ) {
          setError("Invitation has already been accepted");
        } else {
          setError(message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [qrToken]);

  const handleAccept = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const result = await InvitationsService.confirmInvitation(
        qrToken,
        "accept",
      );
      setSuccessMessage(
        result.message || "Access requested, waiting for admin approval",
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to accept invitation";
      if (message.includes("expired")) {
        setActionError("Invitation has expired");
      } else if (message.includes("accepted")) {
        setActionError("Invitation already accepted");
      } else {
        setActionError(message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      const result = await InvitationsService.confirmInvitation(
        qrToken,
        "expired",
      );
      setSuccessMessage(result.message || "Invitation declined");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to decline invitation";
      setActionError(message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div style={{ fontSize: "48px", marginBottom: "1rem" }}>⏳</div>
        <h2>Invitation</h2>
        <p style={{ color: "var(--second-color)" }}>
          Loading invitation details...
        </p>
      </div>
    );
  }

  // Error state (no invitation loaded)
  if (error && !invitation) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div style={{ fontSize: "48px", marginBottom: "1rem" }}>❌</div>
        <h2>Invitation</h2>
        <p style={{ color: "var(--accent-color)" }}>{error}</p>
        <ButtonLarge
          title="← Back to Devices"
          onClick={() => navigate(getPath(ConfigRoutes.DEVICES))}
          mode="black"
          Accent={true}
        />
      </div>
    );
  }

  // Success state
  if (successMessage) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div style={{ fontSize: "48px", marginBottom: "1rem" }}>✅</div>
        <h2>Invitation</h2>
        <p style={{ color: "#28a745", fontWeight: 600, fontSize: "16px" }}>
          {successMessage}
        </p>
        <ButtonLarge
          title="← Back to Devices"
          onClick={() => navigate(getPath(ConfigRoutes.DEVICES))}
          mode="black"
        />
      </div>
    );
  }

  // Main invitation view
  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <div style={{ fontSize: "48px", marginBottom: "1rem" }}>📨</div>
      <h2>Invitation</h2>
      <p style={{ color: "var(--second-color)" }}>
        You have been invited to join a shared workspace.
      </p>

      {/* Invitation info card */}
      {invitation && (
        <div
          style={{
            margin: "1.5rem auto",
            padding: "1.5rem",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            maxWidth: "340px",
            background: "var(--second-background)",
            textAlign: "left",
          }}
        >
          <div style={{ marginBottom: "1rem" }}>
            <span style={{ fontSize: "24px" }}>🏢</span>
            <h3 style={{ margin: "0.5rem 0 0" }}>
              {invitation.targetInstanceName || "Workspace Invitation"}
            </h3>
          </div>

          <table style={{ width: "100%", fontSize: "14px" }}>
            <tbody>
              <tr>
                <td
                  style={{
                    color: "var(--second-color)",
                    padding: "0.5rem 0.5rem 0.5rem 0",
                  }}
                >
                  Workspace
                </td>
                <td style={{ padding: "0.5rem" }}>
                  {invitation.targetInstanceName ||
                    `Instance #${invitation.instanceId}`}
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    color: "var(--second-color)",
                    padding: "0.5rem 0.5rem 0.5rem 0",
                  }}
                >
                  Status
                </td>
                <td style={{ padding: "0.5rem" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: "var(--radius-sm)",
                      fontSize: "12px",
                      fontWeight: 600,
                      background:
                        invitation.status === "pending"
                          ? "#ffc107"
                          : "var(--color-bg-secondary)",
                      color:
                        invitation.status === "pending"
                          ? "#000"
                          : "var(--second-color)",
                    }}
                  >
                    {invitation.status}
                  </span>
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    color: "var(--second-color)",
                    padding: "0.5rem 0.5rem 0.5rem 0",
                  }}
                >
                  Groups
                </td>
                <td style={{ padding: "0.5rem" }}>
                  {invitation.groupNames?.length
                    ? invitation.groupNames.map((g) => g.name).join(", ")
                    : invitation.defaultGroupIds?.length
                      ? invitation.defaultGroupIds.join(", ")
                      : "None"}
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    color: "var(--second-color)",
                    padding: "0.5rem 0.5rem 0.5rem 0",
                  }}
                >
                  Expires
                </td>
                <td style={{ padding: "0.5rem" }}>
                  {formatDate(invitation.expiresAt)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Action error */}
      {actionError && (
        <p
          style={{
            color: "var(--color-danger)",
            fontSize: "14px",
            marginTop: "0.5rem",
          }}
        >
          ❌ {actionError}
        </p>
      )}

      {/* Action buttons */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          justifyContent: "center",
          marginTop: "1.5rem",
        }}
      >
        <ButtonLarge
          title={actionLoading ? "⏳ Processing..." : "✅ Accept Invitation"}
          onClick={handleAccept}
          mode="black"
          isSmall={true}
        />
        <ButtonLarge
          title={actionLoading ? "⏳ Processing..." : "❌ Decline"}
          onClick={handleDecline}
          mode="black"
          Accent={true}
          isSmall={true}
        />
      </div>

      {/* Back to Devices */}
      <ButtonLink
        title="← Back to Devices"
        onClick={() => navigate(getPath(ConfigRoutes.DEVICES))}
      />
    </div>
  );
};
